import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Glossary, Term, Steps, Step } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { ChordBuilder } from "@/components/mt/ChordBuilder";
import { NextUp } from "@/components/Callouts";

export default function MusicTheory19() {
  const { lesson, course, accent } = findLesson("music-theory", "19");
  return (
    <LessonShell
      course={course}
      courseSlug="music-theory"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/music-theory/MT19-Dyads-Triads-Animated.mp4"
        caption="Dyads (2 notes) → Triads (3 notes) — the smallest full chord"
        hint="≈ 24 sec · watch first"
      />
      <Chapter number="19" eyebrow="Music Theory · Chapter Nineteen" title="Every chord begins as a stack">
        <Prose>
          <p>
            From here on, the course is going to study chords in detail — every shape on
            every chart you'll ever read. There are dozens of them, but they're all built
            from the same simple idea: <strong>stack notes a third apart</strong>. Two notes
            stacked is a <em>dyad</em>. Three notes is a <em>triad</em>. Four is a{" "}
            <em>seventh chord</em>. Five or more is an <em>extended chord</em>. The whole
            harmonic universe rests on that ladder.
          </p>
          <p>
            This chapter walks the bottom two rungs: dyads and triads. Get them automatic and
            the seventh chords and jazz extensions of the next chapters will fall in
            effortlessly. Skim them and you'll be guessing every time someone calls a chord
            you've never seen before.
          </p>
          <MTQuote>
            Dyads basically will be two kinds of third groupings, which are a major third or
            a minor third. This is the basis to form any kind of harmony. Even if you throw
            chords out of the window, you can still make music with some beautiful harmony.
          </MTQuote>
        </Prose>
        <NoteImage
          src="/notes/music-theory/finals/19a-interval-chart-all-keys.png"
          caption="Jason's master interval chart — for every starting note, the P5, P4, M3, m3, M6, m6 and tritone are tabulated in a single grid. Memorise the columns and you've memorised the diatonic skeleton of every key in Western music."
        />
      </Chapter>

      <Chapter eyebrow="Dyads" title="Two notes — and you already have music">
        <Prose>
          <p>
            A <strong>dyad</strong> is exactly two notes played together. It's the smallest
            possible chord. You don't even need a triad to make beautiful harmony — throw
            chords out of the window and you can still write a whole song on dyads alone.
          </p>
          <p>
            The two dyads that matter most are the two kinds of <strong>third</strong>:
          </p>
        </Prose>

        <Glossary>
          <Term name="Major third" def="Root + the 3rd note of the major scale. Four semitones up. From C → E. Bright, positive, playful." />
          <Term name="Minor third" def="Root + the ♭3. Three semitones up. From C → E♭. Serious, mellow, less playful." />
          <Term name="Perfect fifth (power chord)" def="Root + the 5th. Seven semitones up. Neutral — neither major nor minor. Strong, open, the root of rock guitar." />
        </Glossary>

        <Prose>
          <p>
            That third dyad — the <strong>perfect fifth</strong> — is what guitarists call a
            "power chord". Two notes, no third, totally ambiguous. It sounds neither major
            nor minor; it sounds <em>strong</em>. And because there's no third inside it,
            you can layer almost any bass note underneath and the whole thing still works.
            Hold a C-G fifth in your right hand and walk your left hand around — the chord
            morphs from add9 to sus to maj7 without ever having to change a finger up top.
          </p>
        </Prose>

        <TheoryBox label="The two thirds — by the numbers">
          <ul className="space-y-1 text-[14px]">
            <li><strong>Major 3rd</strong> — 4 semitones up from the root (e.g. C → E)</li>
            <li><strong>Minor 3rd</strong> — 3 semitones up from the root (e.g. C → E♭)</li>
            <li><strong>Perfect 5th</strong> — 7 semitones up from the root (e.g. C → G)</li>
          </ul>
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="Triads" title="Add a fifth and a chord is born">
        <Prose>
          <p>
            Stack a third on top of another third — total three notes — and you have a
            <strong> triad</strong>. Triads are the workhorses of Western harmony. Every time
            someone writes "C" or "Am" on a chart, they mean a triad. There are four kinds
            you absolutely have to know: <strong>major</strong>, <strong>minor</strong>,{" "}
            <strong>diminished</strong>, and <strong>augmented</strong>.
          </p>
        </Prose>

        <Steps>
          <Step><strong>Major triad</strong> — root + major 3rd + perfect 5th. Bright and victorious. (C major = C E G.)</Step>
          <Step><strong>Minor triad</strong> — root + minor 3rd + perfect 5th. Serious and melancholic. (C minor = C E♭ G.)</Step>
          <Step><strong>Diminished triad</strong> — minor triad + flat the 5th. Tense, unresolved, anxious. (C dim = C E♭ G♭.)</Step>
          <Step><strong>Augmented triad</strong> — major triad + sharp the 5th. Eerie, weightless, suspended. (C aug = C E G♯.)</Step>
        </Steps>

        <Prose>
          <p>
            Notice the symmetry. Major and minor differ only in the third (E vs E♭). Diminished
            and augmented are the "outer edge" of triads — diminished pulls both upper notes
            inward, augmented pushes the top one further outward. Together they form the
            full family of triads built on stacked thirds.
          </p>
          <p>
            Two myths to put to rest. First: minor chords aren't "the ones with a black key
            in the middle". The look of the keys is irrelevant — D minor (D-F-A) is all white
            keys but it's still a minor chord, because the <em>interval</em> from D to F is a
            minor 3rd. Second: you don't need to memorise twelve major triads, twelve minor
            triads, and so on. Memorise <strong>the formula</strong> and you can build any
            triad on any root in seconds.
          </p>
        </Prose>

        <MTQuote>
          The look of the note, or the way you identify the note, is not based on black or
          white. It's based on the vibe created from the root, or the distance from the
          root, or the interval from the root.
        </MTQuote>
      </Chapter>

      <Chapter eyebrow="Suspensions" title="Replace the third — and leave it hanging">
        <Prose>
          <p>
            One special family sits next to the triads: the <strong>suspended chord</strong>.
            Rather than stacking thirds, a sus chord <em>removes</em> the third entirely and
            replaces it with the note next to it.
          </p>
          <ul className="space-y-1 text-[14px]">
            <li><strong>Sus2</strong> — root + major 2nd + perfect 5th. (Csus2 = C D G.) Open and floating.</li>
            <li><strong>Sus4</strong> — root + perfect 4th + perfect 5th. (Csus4 = C F G.) Tense; wants to fall back to the major 3rd.</li>
          </ul>
          <p>
            Sus chords feel <em>unresolved</em> — that's their magic. They live a step
            away from a major triad, leaning toward it: the sus4 falls a semitone to the
            3rd, the sus2 rises a whole tone to it. Sus4 → major is one of the oldest
            cadential moves in pop and worship music. Hold a sus4, then drop the 4 down to
            the 3, and the listener exhales.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="Build them yourself" title="The chord builder">
        <Prose>
          <p>
            Every chord we've named so far is one click away in the builder below. Toggle
            between <em>dyad</em> and <em>triad</em>, choose any root, and the notes light
            up on the keyboard with their interval distances spelled out. Use it like a
            crossword — until building a Dim or Aug on any root takes less than a second
            in your head.
          </p>
        </Prose>

        <ChordBuilder families={["dyad", "triad"]} />
      </Chapter>

      <Chapter eyebrow="Inversions" title="Same chord — different bass">
        <Prose>
          <p>
            Once you have a triad, you can rearrange its three notes around the keyboard
            and it stays the same chord — just <em>inverted</em>. C major has three positions:
          </p>
          <ul className="space-y-1 text-[14px]">
            <li><strong>Root position</strong> — C E G (root on the bottom)</li>
            <li><strong>1st inversion</strong> — E G C (third on the bottom)</li>
            <li><strong>2nd inversion</strong> — G C E (fifth on the bottom)</li>
          </ul>
          <p>
            And then there's <strong>spread voicing</strong> — instead of stacking the three
            notes tightly, you let one note jump down an octave to open the chord wide. A
            spread C major might be C in the left hand, G and E in the right, with air
            between. Inversions and spreads are the difference between a chord that sounds
            functional and a chord that sounds <em>orchestral</em>. The notes don't change.
            The voicing does.
          </p>
        </Prose>

        <TheoryBox label="The inversion principle">
          A triad has three notes — therefore three positions. Whichever note you put in the
          bass, the chord keeps its name. Move the root up to the top and the third is left
          in the bass — that's first inversion. Do it again, and the fifth is in the bass —
          that's second inversion.
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="Beyond the standard four" title="Modal triads — Lydian, Phrygian, cluster">
        <Prose>
          <p>
            Most chords on most charts are major, minor, diminished, augmented, sus2 or
            sus4. But composers reach further. A <strong>Lydian triad</strong> uses 1, 3,
            ♯4 (e.g. C E F♯) to evoke the dreamy Lydian mode. A <strong>Phrygian triad</strong>{" "}
            uses 1, ♭2, 4 (e.g. C D♭ F) for the dark Spanish-flamenco flavour. And a{" "}
            <strong>cluster triad</strong> jams a 2nd <em>between</em> the root and 3rd (e.g.
            C D E) for a chaotic, busy tension. (The Simpsons theme, for the record, is the
            <em>Lydian</em> one — that famous opening leap is the ♯4.)
          </p>
          <p>
            You don't need these on day one. But once you outgrow the basic four, the modal
            and cluster triads are how composers paint with very specific colours. Each one
            is still just three notes — they just happen to be three very deliberate ones.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="What's next" title="Bass-changes and added notes">
        <Prose>
          <p>
            Triads are the trunk. The next two chapters go up the tree. First we look at
            <em> slash chords</em> and <em>add chords</em> — small modifications to a triad
            that change its character without leaving the family. After that, we add a
            fourth note: the world of seventh chords.
          </p>
        </Prose>
        <NoteImage
          src="/notes/music-theory/finals/19b-augmented-triad-symmetry.png"
          caption="The augmented triad is symmetrical — C+ = C E G♯, E+ = E G♯ B♯, A♭+ = A♭ C E. Every one is three stacked major 3rds, so the same three keys on the piano serve as all three chords, respelled. This symmetry is why aug chords sound 'suspended in space' — the ear can't decide which note is the root."
        />
      </Chapter>

      <NextUp
        to="/lesson/music-theory-20"
        label="Chapter 20 · Chord Formation - Slash & Add Chords"
        why="The triad is built. Now we'll change its bass note (slash chords) and bolt extra colour tones onto it (add chords) — without yet leaving the three-note family."
      />
    </LessonShell>
  );
}
